import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import routes from "./routes";
import api from "./api";
import { ViewportProvider } from "./utils";
import { makeServer } from "./server";
import { InternationalizationProvider } from "./features/i18n";
import {
  Fallback,
  NotificationContainer,
  PrivateRoute,
  RouteChangeListener,
} from "./components";
import {
  Dashboard,
  NotFound
} from "./pages";
import AuthPopup from "./features/account/components/auth-popup";
import { useSelector } from "react-redux";
import UnderDevelopmentPopup from "./components/under-development-popup";

const Home = lazy(() => import("./pages/home/home"));
const ProgramDetail = lazy(() => import("./pages/program-detail/program-detail"));
const EventDetail = lazy(() => import("./pages/event-detail/event-detail"));
const ResourceDetail = lazy(() => import("./pages/resource-detail/resource-detail"));
const Landing = lazy(() => import("./pages/landing/landing"));
const ProgramStatisticOrganization = lazy(() => import("./pages/program-statistic-organization/index"));
const ProgramStatistic = lazy(() => import("./pages/program-statistic/program-statistic"));
const OrganizationStatistic = lazy(() => import("./pages/organization-statistic/organization-statistic"));
const MapDownload = lazy(() => import('./pages/map-download/map-download'))
const TicketDetail = lazy(() => import('./pages/ticket-detail/ticket-detail'))
const EventReport = lazy(() => import('./pages/event-report/event-report'))
const ProgramReport = lazy(() => import('./pages/program-report/program-report'))
const OrganizationProfile = lazy(() => import('./pages/organization-profile/organization-profile'))

// This is mock API server, remove this when API available
if (process.env.NODE_ENV === "development" && !api.available) {
  makeServer();
}

const dashboardRoute = {
  path: "/dashboard",
  component: Dashboard,
  exact: true,
  private: true,
  roles: [1, 2]
}

function App() {
  const { showAuth } = useSelector(state => state.account)

  return (
    <>
      <ViewportProvider>
        <InternationalizationProvider>
          <Router>
            <RouteChangeListener />

            {
              showAuth &&
              <AuthPopup />
            }

            {/* A <Switch> looks through its children <Route>s and
              renders the first one that matches the current URL. */}
            <Switch>
              {/* Lazy Loaded Routes */}
              <Route
                path="/organization-profile/:id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <OrganizationProfile {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/program-report/:id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <ProgramReport {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/event-report/:id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <EventReport {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/map-download/:organization_id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <MapDownload {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/ticket/:event_id/:id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <TicketDetail {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/organization-statistic"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <OrganizationStatistic {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/program-statistic-organization/:id"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <ProgramStatisticOrganization {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/program-statistic"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <ProgramStatistic {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/about"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <Landing {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/resource/:id/detail"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <ResourceDetail {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/program/:id/detail"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <ProgramDetail {...props}/>
                  </Suspense>
                )}
              />
              <Route
                path="/event/:id/detail"
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <EventDetail {...props}/>
                  </Suspense>
                )}
              />

              {/* Normal Loaded Routes */}
              {routes.map((route, index) =>
                route.private ? (
                  <PrivateRoute
                    key={index}
                    route={route}
                    path={route.path}
                    render={(props) => (
                      <route.component {...props} {...route} />
                    )}
                  />
                ) : (
                  <Route
                    path={route.path}
                    key={index}
                    render={(props) => (
                      <route.component {...props} {...route} />
                    )}
                  />
                )
              )}

              <PrivateRoute
                route={dashboardRoute}
                path={dashboardRoute.path}
                render={(props) => (
                  <Dashboard {...props} {...dashboardRoute} />
                )}
              />

              <Route
                path="/"
                exact
                render={(props) => (
                  <Suspense fallback={<Fallback />}>
                    <Home {...props}/>
                  </Suspense>
                )}
              />

              <Route component={NotFound}/>
            </Switch>

            {/* Under Development Popup */}
            <UnderDevelopmentPopup />
          </Router>
        </InternationalizationProvider>
      </ViewportProvider>
      <NotificationContainer />
    </>
  );
}

export default App;
